import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Extension, ExtensionSettings } from '../types';
import { getExtensionsWithActivationStatus } from '../lib/extensionsCatalog';
import { activateExtension } from '../lib/userExtensions';
import ExtensionsSkeleton from './ExtensionsSkeleton';
import { loadRumbleSettings } from '../lib/rumbleApi';

interface ExtensionsProps {
  onExtensionsLoaded?: (extensions: Extension[], extensionSettings: ExtensionSettings) => void;
  onNavigateToSettings?: () => void;
}

const Extensions: React.FC<ExtensionsProps> = ({ onExtensionsLoaded, onNavigateToSettings }) => {
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasRumbleApi, setHasRumbleApi] = useState(false);

  const { user } = useAuth();

  // Check if user has Rumble API connected
  useEffect(() => {
    const checkRumbleApi = async () => {
      if (user) {
        try {
          const settings = await loadRumbleSettings();
          setHasRumbleApi(settings?.api_key && settings?.rumble_username ? true : false);
        } catch (err) {
          setHasRumbleApi(false);
        }
      }
    };
    checkRumbleApi();
  }, [user]);

  // Load extensions on component mount
  useEffect(() => {
    loadExtensions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExtensions = async () => {
    try {
      setLoading(true);
      setError(null);
      const { extensions: loadedExtensions, extensionSettings } = await getExtensionsWithActivationStatus();
      setExtensions(loadedExtensions);
      
      // Notify parent component of loaded data
      if (onExtensionsLoaded) {
        onExtensionsLoaded(loadedExtensions, extensionSettings);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load extensions');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateExtension = async (extensionId: string) => {
    const extension = extensions.find(ext => ext.id === extensionId);
    if (!extension || extension.comingSoon || extension.activated) return;

    try {
      setError(null);
      await activateExtension(extensionId);

      // Reload extensions to get updated activation status
      await loadExtensions();
    } catch (err) {
      // Check for specific error types
      if (err instanceof Error && err.message === 'NO_RUMBLE_API') {
        setError(
          <>
            You need to connect your Rumble Live Stream API in the{' '}
            <button
              onClick={() => onNavigateToSettings?.()}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Settings
            </button>
            {' '}tab before activating extensions.
          </>
        );
      } else if (err instanceof Error && err.message === 'NOT_WHITELISTED') {
        setError(
          <>
            You need to be whitelisted to activate this extension. Please reach out to{' '}
            <a
              href="https://x.com/whizkick"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              @whizkick on X
            </a>
            {' '}for access.
          </>
        );
      } else {
        // Show the server-side error message to the user
        const errorMessage = err instanceof Error ? err.message : 'Failed to activate extension';
        setError(errorMessage);
      }
      console.error('Extension activation error:', err);
    }
  };

  // Filter and sort extensions based on search query
  const filteredExtensions = extensions
    .filter(extension =>
      extension.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      extension.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Sort priority: 1. Activated, 2. Active (not coming soon), 3. Coming soon
      if (a.activated && !b.activated) return -1;
      if (!a.activated && b.activated) return 1;

      if (!a.comingSoon && b.comingSoon) return -1;
      if (a.comingSoon && !b.comingSoon) return 1;

      return 0; // Keep original order if same priority
    });

  // Show skeleton while loading
  if (loading) {
    return <ExtensionsSkeleton />;
  }
  return (
    <section id="extensions" className="content-section">
      {/* Closed Beta Alert */}
      {!user ? (
        <div className="mb-6 p-4 border-2 border-yellow-500 bg-yellow-900 bg-opacity-20 text-yellow-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-yellow-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Closed Beta</p>
              <p className="text-sm">
                Dork AI Bot is currently in closed beta. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for access.
              </p>
            </div>
          </div>
        </div>
      ) : !hasRumbleApi ? (
        <div className="mb-6 p-4 border-2 border-yellow-500 bg-yellow-900 bg-opacity-20 text-yellow-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-yellow-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Setup Required</p>
              <p className="text-sm mb-2">
                Before activating extensions, you need to connect your Rumble Live Stream API in{' '}
                <button
                  onClick={() => onNavigateToSettings?.()}
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  Settings
                </button>
                .
              </p>
              <p className="text-sm">
                You'll also need to be whitelisted for closed beta access. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 border-2 border-blue-500 bg-blue-900 bg-opacity-20 text-blue-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-blue-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Closed Beta Access</p>
              <p className="text-sm">
                Extensions require whitelist access. If you encounter issues activating an extension, please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-4 mb-6 header-border pb-4">
        <h2 className="text-xl font-semibold text-white">extension catalog</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="search..."
            className="paint-input w-full sm:w-64 pr-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="fa-solid fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"></i>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
          <i className="fa-solid fa-exclamation-circle mr-2"></i>
          {error}
          <button 
            onClick={loadExtensions}
            className="ml-2 text-blue-400 hover:text-blue-300 underline"
          >
            Retry
          </button>
        </div>
      )}
      
      <div id="extensions-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredExtensions.map(extension => (
          <div 
            key={extension.id}
            className={`paint-card ${extension.comingSoon ? 'opacity-50' : ''} relative`}
            data-id={extension.id}
            data-name={extension.name}
          >
            {extension.is_paid && (
              <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-semibold">
                <i className="fa-solid fa-crown mr-1"></i>
                PAID
              </div>
            )}
            <div className="card-icon-placeholder">
              <i className={`${extension.icon} fa-3x text-white`}></i>
            </div>
            <div className="card-content">
              <h4 className="font-semibold mb-1 text-white flex items-center gap-2">
                {extension.name}
                {extension.is_paid && (
                  <i className="fa-solid fa-lock text-yellow-500 text-xs"></i>
                )}
              </h4>
              <p className="text-xs text-gray-400">{extension.description}</p>
              <button 
                className={`paint-button w-full text-sm ${extension.activated ? 'activated' : ''} ${extension.comingSoon || extension.activated ? 'disabled' : ''}`}
                onClick={() => handleActivateExtension(extension.id)}
                disabled={extension.comingSoon || extension.activated}
              >
                {extension.comingSoon ? 'coming soon' : (extension.activated ? 'activated' : 'activate')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredExtensions.length === 0 && !loading && !error && (
        <div className="text-center p-8">
          <i className="fa-solid fa-search text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-semibold text-white mb-2">No Extensions Found</h3>
          <p className="text-gray-400">
            {searchQuery ? `No extensions match "${searchQuery}"` : 'No extensions available'}
          </p>
        </div>
      )}
    </section>
  );
};

export default Extensions;